import type { BaseMunicipios, Municipio } from "@/lib/municipios";
import type { MapaClientes } from "./api";

// Localização dos clientes para o mapa da tela Clientes. O código IBGE (resolvido pelo CEP
// no instituicao) vale primeiro; sem ele, o município/UF digitado no cadastro é casado por
// nome com a tabela IBGE local. Nada disso é gravado: quando o CEP for resolvido, o código
// passa a valer sozinho.

export interface CidadeClientes {
  municipio: Municipio;
  clientes: number;
}

export interface ClientesResolvidos {
  cidades: CidadeClientes[];
  /** Sem código e sem nome que case, ou código fora da tabela local. */
  semLocalizacao: number;
}

/** Só letras e dígitos, sem acento e em minúsculas: "Sant'Ana" = "Santana", "São Paulo " = "sao paulo". */
export function normalizarNome(nome: string): string {
  return nome
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

const SUFIXO_UF = /\s*[-/(,]\s*[A-Za-z]{2}\)?\s*$/;

const indices = new WeakMap<BaseMunicipios, Map<string, Municipio[]>>();

/** nome normalizado -> municípios com esse nome (vários estados podem ter o mesmo). */
function indicePorNome(base: BaseMunicipios): Map<string, Municipio[]> {
  let indice = indices.get(base);
  if (!indice) {
    indice = new Map();
    for (const daUf of base.porUf.values()) {
      for (const municipio of daUf) {
        const chave = normalizarNome(municipio.nome);
        indice.set(chave, [...(indice.get(chave) ?? []), municipio]);
      }
    }
    indices.set(base, indice);
  }
  return indice;
}

/**
 * Município do cadastro pelo nome. Com UF, só aceita o daquela UF; sem UF, só se o nome for
 * único no país. Também tira um "- SP" / "/SP" digitado junto do nome.
 */
export function casarMunicipio(base: BaseMunicipios, municipio: string, estado?: string | null): Municipio | undefined {
  const indice = indicePorNome(base);
  const uf = estado?.trim().toUpperCase() || undefined;
  for (const nome of [municipio, municipio.replace(SUFIXO_UF, "")]) {
    const candidatos = indice.get(normalizarNome(nome));
    if (!candidatos) continue;
    const casado = uf ? candidatos.find((c) => c.uf === uf) : candidatos.length === 1 ? candidatos[0] : undefined;
    if (casado) return casado;
  }
  return undefined;
}

export function resolverClientes(mapa: MapaClientes, base: BaseMunicipios): ClientesResolvidos {
  const porCodigo = new Map<string, CidadeClientes>();
  let semLocalizacao = mapa.clientesSemLocalizacao;

  const somar = (municipio: Municipio, clientes: number) => {
    const atual = porCodigo.get(municipio.codigoIbge);
    if (atual) atual.clientes += clientes;
    else porCodigo.set(municipio.codigoIbge, { municipio, clientes });
  };

  for (const cidade of mapa.cidades) {
    const municipio = base.porCodigo.get(cidade.codigoIbge);
    if (municipio) somar(municipio, cidade.clientes);
    else semLocalizacao += cidade.clientes;
  }

  for (const grupo of mapa.semCodigo ?? []) {
    const municipio = casarMunicipio(base, grupo.municipio, grupo.estado);
    if (!municipio) continue;
    somar(municipio, grupo.clientes);
    semLocalizacao -= grupo.clientes;
  }

  const cidades = [...porCodigo.values()].sort(
    (a, b) => b.clientes - a.clientes || a.municipio.nome.localeCompare(b.municipio.nome, "pt-BR"),
  );
  return { cidades, semLocalizacao };
}
